import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUpload, FiDownload, FiImage, FiSettings, FiRotateCw, FiCheck, FiX, FiMaximize2, FiInfo, FiZap, FiChevronDown } = FiIcons;

const PRESET_RATIOS = [
  { name: 'Original', ratio: null, description: 'Keep original proportions' },
  { name: 'Square (1:1)', ratio: 1, description: 'Perfect square' },
  { name: 'Widescreen (16:9)', ratio: 16/9, description: 'Video format' },
  { name: 'Standard (4:3)', ratio: 4/3, description: 'Classic format' },
  { name: 'Photography (3:2)', ratio: 3/2, description: 'DSLR format' },
  { name: 'Portrait (9:16)', ratio: 9/16, description: 'Mobile/Story' },
  { name: 'Banner (21:9)', ratio: 21/9, description: 'Ultra-wide' },
];

const SIZE_PRESETS = [
  { name: 'Custom Size', width: 0, height: 0 },
  { name: 'Instagram Post (1080×1080)', width: 1080, height: 1080 },
  { name: 'Instagram Story (1080×1920)', width: 1080, height: 1920 },
  { name: 'Facebook Cover (1200×630)', width: 1200, height: 630 },
  { name: 'Twitter Header (1500×500)', width: 1500, height: 500 },
  { name: 'LinkedIn Banner (1584×396)', width: 1584, height: 396 },
  { name: 'YouTube Thumbnail (1280×720)', width: 1280, height: 720 },
  { name: 'Profile Picture (400×400)', width: 400, height: 400 },
  { name: 'Website Header (1920×600)', width: 1920, height: 600 },
];

const QUALITY_OPTIONS = [
  { name: 'Maximum Quality', value: 1.0 },
  { name: 'High Quality', value: 0.9 },
  { name: 'Good Quality', value: 0.8 },
  { name: 'Compressed', value: 0.7 },
];

function ImageResizer() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [resizedImage, setResizedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  
  // Settings
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [maintainAspect, setMaintainAspect] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState(SIZE_PRESETS[0]);
  const [selectedRatio, setSelectedRatio] = useState(PRESET_RATIOS[0]);
  const [selectedQuality, setSelectedQuality] = useState(QUALITY_OPTIONS[1]);
  const [format, setFormat] = useState('jpeg');
  
  // UI States
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  const handleFileSelect = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please select a valid image file (JPG, PNG, WebP, GIF)');
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB limit
      alert('File size too large. Please select an image under 50MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setOriginalImage(img);
        setSelectedImage(e.target.result);
        setWidth(img.width.toString());
        setHeight(img.height.toString());
        setResizedImage(null);
        setSelectedPreset(SIZE_PRESETS[0]);
        setSelectedRatio(PRESET_RATIOS[0]);
      };
      img.onerror = () => {
        alert('Error loading image. Please try another file.');
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const calculateDimensions = useCallback((newWidth, newHeight, ratio) => {
    if (!originalImage) return { width: newWidth, height: newHeight };

    const originalRatio = originalImage.width / originalImage.height;

    if (ratio !== null) {
      if (newWidth && !newHeight) {
        return { width: parseInt(newWidth), height: Math.round(parseInt(newWidth) / ratio) };
      } else if (newHeight && !newWidth) {
        return { width: Math.round(parseInt(newHeight) * ratio), height: parseInt(newHeight) };
      } else if (newWidth && newHeight) {
        return { width: parseInt(newWidth), height: Math.round(parseInt(newWidth) / ratio) };
      }
    } else if (maintainAspect) {
      if (newWidth && !newHeight) {
        return { width: parseInt(newWidth), height: Math.round(parseInt(newWidth) / originalRatio) };
      } else if (newHeight && !newWidth) {
        return { width: Math.round(parseInt(newHeight) * originalRatio), height: parseInt(newHeight) };
      }
    }

    return { 
      width: parseInt(newWidth) || originalImage.width, 
      height: parseInt(newHeight) || originalImage.height 
    };
  }, [originalImage, maintainAspect]);

  const handleWidthChange = (newWidth) => {
    setWidth(newWidth);
    if (maintainAspect || selectedRatio.ratio !== null) {
      const dimensions = calculateDimensions(newWidth, null, selectedRatio.ratio);
      setHeight(dimensions.height.toString());
    }
  };

  const handleHeightChange = (newHeight) => {
    setHeight(newHeight);
    if (maintainAspect || selectedRatio.ratio !== null) {
      const dimensions = calculateDimensions(null, newHeight, selectedRatio.ratio);
      setWidth(dimensions.width.toString());
    }
  };

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    if (preset.width > 0 && preset.height > 0) {
      setWidth(preset.width.toString());
      setHeight(preset.height.toString());
    }
  };

  const handleRatioChange = (ratio) => {
    setSelectedRatio(ratio);
    if (ratio.ratio !== null && originalImage) {
      const currentWidth = parseInt(width) || originalImage.width;
      const dimensions = calculateDimensions(currentWidth.toString(), null, ratio.ratio);
      setWidth(dimensions.width.toString());
      setHeight(dimensions.height.toString());
    }
  };

  const resizeImage = async () => {
    if (!originalImage || !width || !height) return;

    setIsProcessing(true);
    setDownloadProgress(0);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      const newWidth = parseInt(width);
      const newHeight = parseInt(height);
      
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Configure canvas for high quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      setDownloadProgress(25);

      // For very large images, use step-down scaling for better quality
      if (originalImage.width > newWidth * 2 || originalImage.height > newHeight * 2) {
        const steps = Math.ceil(Math.log2(Math.max(originalImage.width / newWidth, originalImage.height / newHeight)));
        let currentCanvas = document.createElement('canvas');
        let currentCtx = currentCanvas.getContext('2d');
        
        currentCanvas.width = originalImage.width;
        currentCanvas.height = originalImage.height;
        currentCtx.drawImage(originalImage, 0, 0);

        for (let i = 0; i < steps; i++) {
          const ratio = Math.pow(0.5, i + 1);
          const stepWidth = Math.max(newWidth, originalImage.width * ratio);
          const stepHeight = Math.max(newHeight, originalImage.height * ratio);
          
          const nextCanvas = document.createElement('canvas');
          const nextCtx = nextCanvas.getContext('2d');
          nextCanvas.width = stepWidth;
          nextCanvas.height = stepHeight;
          
          nextCtx.imageSmoothingEnabled = true;
          nextCtx.imageSmoothingQuality = 'high';
          nextCtx.drawImage(currentCanvas, 0, 0, stepWidth, stepHeight);
          
          currentCanvas = nextCanvas;
          currentCtx = nextCtx;
          
          setDownloadProgress(25 + (i / steps) * 50);
        }

        ctx.drawImage(currentCanvas, 0, 0, newWidth, newHeight);
      } else {
        ctx.drawImage(originalImage, 0, 0, newWidth, newHeight);
      }

      setDownloadProgress(75);

      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          setResizedImage(url);
          setDownloadProgress(100);
          
          if (resizedImage) {
            URL.revokeObjectURL(resizedImage);
          }
        }
        setIsProcessing(false);
      }, `image/${format}`, selectedQuality.value);

    } catch (error) {
      console.error('Error during resizing:', error);
      alert('Error resizing image. Please try again.');
      setIsProcessing(false);
      setDownloadProgress(0);
    }
  };

  const downloadImage = () => {
    if (!resizedImage) return;

    const link = document.createElement('a');
    link.href = resizedImage;
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    link.download = `resized_${width}x${height}_${timestamp}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (typeof gtag !== 'undefined') {
      gtag('event', 'download', {
        event_category: 'Image Resizer',
        event_label: `${width}x${height}_${format}`,
        value: 1
      });
    }
  };

  const resetImage = () => {
    if (resizedImage) {
      URL.revokeObjectURL(resizedImage);
    }
    setSelectedImage(null);
    setOriginalImage(null);
    setResizedImage(null);
    setWidth('');
    setHeight('');
    setSelectedPreset(SIZE_PRESETS[0]);
    setSelectedRatio(PRESET_RATIOS[0]);
    setDownloadProgress(0);
  };

  const getFileSizeEstimate = () => {
    if (!width || !height) return '';
    const pixels = parseInt(width) * parseInt(height);
    const bytesPerPixel = format === 'png' ? 4 : 3;
    const estimatedSize = (pixels * bytesPerPixel * selectedQuality.value) / 1024;
    
    if (estimatedSize > 1024) {
      return `~${(estimatedSize / 1024).toFixed(1)}MB`;
    }
    return `~${estimatedSize.toFixed(0)}KB`;
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h2 className="text-4xl font-bold text-gray-800 mb-3">
          Free Image Resizer
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Resize your images instantly with professional quality. Fast, secure, and completely free.
        </p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Upload Section */}
        <div className="space-y-6">
          {/* Upload Area */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
          >
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 cursor-pointer ${
                isDragging
                  ? 'border-blue-500 bg-blue-50 scale-[1.02]'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
              }`}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <motion.div
                animate={isDragging ? { scale: 1.05 } : { scale: 1 }}
                transition={{ duration: 0.2 }}
              >
                <SafeIcon icon={FiUpload} className="text-4xl text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-700 mb-2">
                  {isDragging ? 'Drop your image here!' : 'Drag & drop or click to upload'}
                </p>
                <p className="text-sm text-gray-500">
                  JPG, PNG, WebP, GIF • Max 50MB
                </p>
              </motion.div>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              className="hidden"
            />
          </motion.div>

          {/* Original Image Preview */}
          <AnimatePresence>
            {selectedImage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-800">Original Image</h4>
                  {originalImage && (
                    <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      {originalImage.width} × {originalImage.height}
                    </span>
                  )}
                </div>
                <div className="relative group">
                  <img
                    src={selectedImage}
                    alt="Original"
                    className="w-full h-auto rounded-lg shadow-sm transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Controls and Preview Section */}
        <div className="space-y-6">
          {/* Compact Settings Panel */}
          <AnimatePresence>
            {selectedImage && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
              >
                <h4 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                  <SafeIcon icon={FiSettings} className="mr-2 text-blue-600" />
                  Resize Settings
                </h4>

                <div className="space-y-4">
                  {/* Size Preset Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Size Preset
                    </label>
                    <div className="relative">
                      <select
                        value={selectedPreset.name}
                        onChange={(e) => {
                          const preset = SIZE_PRESETS.find(p => p.name === e.target.value);
                          handlePresetChange(preset);
                        }}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-10"
                      >
                        {SIZE_PRESETS.map((preset, index) => (
                          <option key={index} value={preset.name}>
                            {preset.name}
                          </option>
                        ))}
                      </select>
                      <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Custom Dimensions */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Width (px)
                      </label>
                      <input
                        type="number"
                        value={width}
                        onChange={(e) => handleWidthChange(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Width"
                        min="1"
                        max="10000"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Height (px)
                      </label>
                      <input
                        type="number"
                        value={height}
                        onChange={(e) => handleHeightChange(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Height"
                        min="1"
                        max="10000"
                      />
                    </div>
                  </div>

                  {/* Aspect Ratio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aspect Ratio
                    </label>
                    <div className="relative">
                      <select
                        value={selectedRatio.name}
                        onChange={(e) => {
                          const ratio = PRESET_RATIOS.find(r => r.name === e.target.value);
                          handleRatioChange(ratio);
                        }}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-10"
                      >
                        {PRESET_RATIOS.map((ratio, index) => (
                          <option key={index} value={ratio.name}>
                            {ratio.name}
                          </option>
                        ))}
                      </select>
                      <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  {/* Quick Settings Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quality
                      </label>
                      <div className="relative">
                        <select
                          value={selectedQuality.name}
                          onChange={(e) => {
                            const quality = QUALITY_OPTIONS.find(q => q.name === e.target.value);
                            setSelectedQuality(quality);
                          }}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-10"
                        >
                          {QUALITY_OPTIONS.map((quality, index) => (
                            <option key={index} value={quality.name}>
                              {quality.name}
                            </option>
                          ))}
                        </select>
                        <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Format
                      </label>
                      <div className="relative">
                        <select
                          value={format}
                          onChange={(e) => setFormat(e.target.value)}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none pr-10"
                        >
                          <option value="jpeg">JPEG</option>
                          <option value="png">PNG</option>
                          <option value="webp">WebP</option>
                        </select>
                        <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  {/* Info Panel */}
                  {width && height && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center text-sm text-blue-800">
                        <SafeIcon icon={FiInfo} className="mr-2 flex-shrink-0" />
                        <span>Output: {width}×{height} • Estimated size: {getFileSizeEstimate()}</span>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={resizeImage}
                      disabled={isProcessing || !width || !height}
                      className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center relative overflow-hidden"
                    >
                      {isProcessing ? (
                        <>
                          <div className="absolute inset-0 bg-blue-700" style={{ width: `${downloadProgress}%` }} />
                          <div className="relative flex items-center">
                            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
                            {downloadProgress}%
                          </div>
                        </>
                      ) : (
                        <>
                          <SafeIcon icon={FiZap} className="mr-2" />
                          Resize Image
                        </>
                      )}
                    </motion.button>
                    
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={resetImage}
                      className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200"
                    >
                      <SafeIcon icon={FiX} />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Resized Image Preview */}
          <AnimatePresence>
            {resizedImage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-gray-800">Resized Image</h4>
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {width} × {height}
                  </span>
                </div>
                <div className="relative group">
                  <img
                    src={resizedImage}
                    alt="Resized"
                    className="w-full h-auto rounded-lg shadow-sm transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={downloadImage}
                  className="w-full mt-4 bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-all duration-200 flex items-center justify-center"
                >
                  <SafeIcon icon={FiDownload} className="mr-2" />
                  Download Image
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Tips Panel */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-6 border border-gray-100"
          >
            <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <SafeIcon icon={FiInfo} className="mr-2 text-blue-600" />
              Quick Tips
            </h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2 mt-1 text-xs">●</span>
                Use PNG for images with transparency
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2 mt-1 text-xs">●</span>
                JPEG is best for photos and smaller file sizes
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2 mt-1 text-xs">●</span>
                WebP offers the best compression and quality
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2 mt-1 text-xs">●</span>
                All processing happens locally in your browser
              </li>
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Hidden canvas for processing */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}

export default ImageResizer;